import { createApp } from "vue";
import { pinia } from "./pinia";
import axios from "axios";
import VueAxios from "vue-axios";

import App from "./App.vue";
import router from "./router";
import apis from "./request/api";
import enums from "./enums";
import funcs from "./funcs";
import filters from "./filters";
import stores from "./stores";
import currencyFilter from "./filters/currency";
import discountFilter from "./filters/discount";
import i18n from "./i18n/i18n";
import ImagePlugin from "./plugins/imagePlugin"; // 引入插件
import storePlugin from "./plugins/storePlugin";
import Loading from "vue3-loading-overlay";
import "vue3-loading-overlay/dist/vue3-loading-overlay.css";
import { saveAs } from "file-saver";

export const CHUNK_RELOAD_KEY = "__chunk_reload_state__";
export const CHUNK_FAILURE_EVENT = "app:chunk-load-failed";
export const CHUNK_RECOVERED_EVENT = "app:chunk-load-recovered";

document.addEventListener(
  "blur",
  (event) => {
    if (
      event.target.tagName === "INPUT" &&
      ["text", "tel"].includes(event.target.type)
    ) {
      const trimmedValue = event.target.value.trim();
      if (trimmedValue !== event.target.value) {
        event.target.value = trimmedValue;

        // 觸發 Vue 的 v-model 更新
        const eventInput = new Event("input", { bubbles: true });
        event.target.dispatchEvent(eventInput);
      }
    }
  },
  true
); // 使用捕獲階段，確保能處理所有元素
document.addEventListener(
  "keydown",
  (event) => {
    if (
      event.key === "Enter" &&
      event.target.tagName === "INPUT" &&
      ["text", "tel"].includes(event.target.type)
    ) {
      const trimmedValue = event.target.value.trim();
      if (trimmedValue !== event.target.value) {
        event.target.value = trimmedValue;

        const eventInput = new Event("input", { bubbles: true });
        event.target.dispatchEvent(eventInput);
      }
    }
  },
  true
);

const app = createApp(App);

// === 全域 ChunkLoadError / 資源版本不一致 保險 ===
(function setupChunkLoadGuard() {
  const CHUNK_ERROR_PATTERNS = [
    /ChunkLoadError/i,
    /Loading chunk [\w-]+ failed/i,
    /Loading CSS chunk [\w-]+ failed/i,
    /Failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /Unexpected token '<'/i,
  ];
  const RETRY_WINDOW_MS = 60 * 1000;
  const MAX_RELOAD_COUNT = 1;

  function readReloadState() {
    try {
      return JSON.parse(sessionStorage.getItem(CHUNK_RELOAD_KEY) || "null");
    } catch (error) {
      return null;
    }
  }

  function writeReloadState(state) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, JSON.stringify(state));
  }

  function clearReloadState() {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    window.dispatchEvent(new CustomEvent(CHUNK_RECOVERED_EVENT));
  }

  function isChunkAssetTarget(target) {
    const src = target?.src || target?.href || "";
    return /\/(js|css)\/.+\.(js|css)(\?|$)/i.test(src);
  }

  function isChunkLoadFailure(reason, target) {
    const text = String(reason || "");
    if (CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text))) {
      return true;
    }

    return isChunkAssetTarget(target);
  }

  function dispatchChunkFailure(detail) {
    window.dispatchEvent(
      new CustomEvent(CHUNK_FAILURE_EVENT, {
        detail,
      })
    );
  }

  function reloadWithCacheBusting() {
    try {
      const url = new URL(location.href);
      url.searchParams.set("_v", Date.now().toString());
      location.replace(url.toString());
    } catch (error) {
      location.reload();
    }
  }

  function tryRecover(reason, extra = {}) {
    if (!isChunkLoadFailure(reason, extra.target)) return;

    const now = Date.now();
    const reloadState = readReloadState();
    const isRecentFailure =
      reloadState && now - Number(reloadState.ts || 0) < RETRY_WINDOW_MS;
    const reloadCount = isRecentFailure ? Number(reloadState.count || 0) : 0;
    const detail = {
      message: String(reason || "Chunk load failed"),
      resource: extra.target?.src || extra.target?.href || "",
      reloadCount,
    };

    if (reloadCount < MAX_RELOAD_COUNT) {
      writeReloadState({
        count: reloadCount + 1,
        ts: now,
      });
      dispatchChunkFailure({
        ...detail,
        phase: "reload",
      });
      alert("系統已更新，將為您重新載入頁面。");
      reloadWithCacheBusting();
      return;
    }

    dispatchChunkFailure({
      ...detail,
      phase: "fallback",
    });
  }

  router.onError((err) => {
    tryRecover(err?.message || err);
  });

  window.addEventListener("unhandledrejection", (event) => {
    tryRecover(event?.reason?.message || event?.reason);
  });

  window.addEventListener("error", (event) => {
    tryRecover(event?.message || event?.error?.message || event?.error, {
      target: event?.target,
    });
  });

  router.afterEach((to, from, failure) => {
    // lazy route 的元件也成功載入後，才能視為版本不一致已恢復。
    // 若在應用程式啟動時就清除，後續載入同一個失效 chunk 可能反覆重新整理。
    if (!failure && readReloadState()) {
      clearReloadState();
    }
  });
})();

app.config.globalProperties.$currencyFilter = currencyFilter;
app.config.globalProperties.$discountFilter = discountFilter;
app.config.globalProperties.$api = apis;
app.config.globalProperties.$enum = enums;
app.config.globalProperties.$func = funcs;
app.config.globalProperties.$filter = filters;
app.config.globalProperties.$store = stores;
app.config.globalProperties.$fileSaver = saveAs;

app.use(VueAxios, axios);
app.use(router);
app.use(stores);
app.use(i18n);
app.use(ImagePlugin); // 閮餃??辣
app.use(storePlugin); // 閮餃??辣
app.use(pinia);
app.component("LoadingOverlay", Loading);

app.mount("#app");
